import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

// ── 型定義 (BE と整合) ──

export type InspectionAssetType = "section" | "equipment";
export type InspectionType = "legal" | "voluntary" | "daily" | "special";
export type InspectionStatus = "予定" | "完了" | "未実施";
export type InspectionResult = "合格" | "条件付合格" | "不合格";
export type InspectionFrequency =
  | "monthly"
  | "quarterly"
  | "semi-annual"
  | "annual"
  | "triennial"
  | "decennial";
export type LegalRequiredFlag = "要" | "否" | "該当なし";

export interface InspectionDocument {
  document_id: string;
  file_name: string;
  file_type: string;
  s3_key: string;
  uploaded_at: string;
}

export interface InspectionRecord {
  client_id: string;
  sort_key: string;
  inspection_id: string;
  property_id: string;

  asset_id: string;
  asset_type: InspectionAssetType;

  type: InspectionType;
  type_name: string;

  scheduled_date: string;
  month: string;
  frequency?: InspectionFrequency;

  status: InspectionStatus;
  result?: InspectionResult;
  executed_date?: string;
  inspector?: string;
  findings?: string;
  actions?: string;

  legal_required?: LegalRequiredFlag;
  correction_date?: string;
  legal_basis?: string;
  is_remote?: boolean;
  is_multiple?: boolean;

  linked_repair_id?: string;
  linked_management_task_id?: string;

  documents?: InspectionDocument[];

  created_at: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface CreateInspectionBody {
  property_id: string;
  asset_id: string;
  asset_type: InspectionAssetType;
  type: InspectionType;
  type_name: string;
  scheduled_date: string;
  frequency?: InspectionFrequency;
  status?: InspectionStatus;
  inspector?: string;
  findings?: string;
  actions?: string;
  legal_required?: LegalRequiredFlag;
  legal_basis?: string;
  is_remote?: boolean;
  is_multiple?: boolean;
  documents?: InspectionDocument[];
}

export interface UpdateInspectionBody {
  type?: InspectionType;
  type_name?: string;
  scheduled_date?: string;
  frequency?: InspectionFrequency;
  status?: InspectionStatus;
  result?: InspectionResult;
  executed_date?: string;
  inspector?: string;
  findings?: string;
  actions?: string;
  legal_required?: LegalRequiredFlag;
  correction_date?: string;
  legal_basis?: string;
  is_remote?: boolean;
  is_multiple?: boolean;
  linked_repair_id?: string;
  linked_management_task_id?: string;
  documents?: InspectionDocument[];
}

interface InspectionState {
  list: {
    data: InspectionRecord[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: InspectionState = {
  list: { data: [], loading: false, error: null },
};

// ── Thunks ──

export const fetchInspections = createAsyncThunk<
  InspectionRecord[],
  {
    property_id: string;
    asset_id?: string;
    asset_type?: InspectionAssetType;
    year?: string;
    month?: string;
    type?: InspectionType;
    status?: InspectionStatus;
  }
>("inspections/fetch", async (params, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/inspections`, { params });
    return res.data?.data?.items || [];
  } catch {
    return rejectWithValue("点検データの取得に失敗しました");
  }
});

export const createInspection = createAsyncThunk<
  InspectionRecord,
  CreateInspectionBody
>("inspections/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/inspections`, body);
    return res.data.data;
  } catch {
    return rejectWithValue("点検の登録に失敗しました");
  }
});

export const updateInspection = createAsyncThunk<
  InspectionRecord,
  {
    asset_id: string;
    scheduled_date: string;
    inspection_id: string;
    body: UpdateInspectionBody;
  }
>(
  "inspections/update",
  async (
    { asset_id, scheduled_date, inspection_id, body },
    { rejectWithValue },
  ) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/inspections/${asset_id}/${scheduled_date}/${inspection_id}`,
        body,
      );
      return res.data.data;
    } catch {
      return rejectWithValue("点検の更新に失敗しました");
    }
  },
);

export const deleteInspection = createAsyncThunk<
  { asset_id: string; scheduled_date: string; inspection_id: string },
  { asset_id: string; scheduled_date: string; inspection_id: string }
>(
  "inspections/delete",
  async (params, { rejectWithValue }) => {
    try {
      await ipponApi.delete(
        `${BaseURL}/api/v1/inspections/${params.asset_id}/${params.scheduled_date}/${params.inspection_id}`,
      );
      return params;
    } catch {
      return rejectWithValue("点検の削除に失敗しました");
    }
  },
);

const inspectionSlice = createSlice({
  name: "inspections",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInspections.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchInspections.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchInspections.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = (action.payload as string) || null;
      })
      .addCase(createInspection.fulfilled, (state, action) => {
        state.list.data.push(action.payload);
      })
      .addCase(updateInspection.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (i) => i.inspection_id === action.payload.inspection_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
        else state.list.data.push(action.payload);
      })
      .addCase(deleteInspection.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (i) => i.inspection_id !== action.payload.inspection_id,
        );
      });
  },
});

export default inspectionSlice.reducer;
